import React, {useEffect, useState} from 'react';
import {Grid, CircularProgress, Typography} from '@material-ui/core';
import {Show} from 'components/utils/Show';
import AlertDialog from './AlertDialog';
import {CloudErrorIcon, SuccessIcon, ErrorIcon} from './icons/icons';
import {H4, P} from './TypographyShortcuts';
import {PSingleLine} from './TypographyShortcuts';

const CenteredContainer = ({children, ...props}) => {
    return (
        <Grid
            container
            justifyContent='center'
            alignContent='center'
            alignItems='center'
            style={{
                height: '100%',
                width: '100%',
            }}
            spacing={2}
            {...props}
        >
            {children}
        </Grid>
    );
};

const CenteredColumnItem = ({children}) => {
    return (
        <Grid item container xs={12} justifyContent='center'>
            <Grid item>{children}</Grid>
        </Grid>
    );
};

let TIMEOUT = 60000;

const LoadingPage = ({message}) => {
    const [timer, setTimer] = useState();
    const [timeoutError, setTimeoutError] = useState(false);

    useEffect(() => {
        setTimer(
            setTimeout(() => {
                setTimeoutError(
                    'The cat got stuck in the toaster, please refresh to try again. Let us know if you get stuck as well!'
                );
            }, TIMEOUT)
        );
        return () => {
            clearTimeout(timer);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <CenteredContainer>
            <Show condition={message || timeoutError}>
                <CenteredColumnItem>
                    <Typography variant='overline' display='block' gutterBottom>
                        {message || timeoutError}
                    </Typography>
                </CenteredColumnItem>
            </Show>
            <Show condition={!message && !timeoutError}>
                <CenteredColumnItem>
                    <CircularProgress />
                </CenteredColumnItem>
            </Show>
        </CenteredContainer>
    );
};

const ErrorPage = ({
    title = 'Oops! Something went wrong...',
    message = 'Our team is working to get this page back in action. Please try again later',
    children,
    error = null,
}) => {
    const development = process.env.NODE_ENV === 'development';

    if (!development && error) {
        window.dataLayer.push({
            event: 'generic_error',
            data: {
                url: window.location.href,
                endpoint: error?.endpoint,
                exception: error?.exception,
                message: error?.message,
                status: error?.status,
            },
        });
    }

    return (
        <CenteredContainer>
            <CenteredColumnItem>
                <ErrorIcon height='100px' width='100px' />
            </CenteredColumnItem>
            <CenteredColumnItem>
                <H4 gutterBottom={false} align='center'>
                    {title}
                </H4>
            </CenteredColumnItem>
            {message && (
                <CenteredColumnItem>
                    <P align='center'>{message}</P>
                </CenteredColumnItem>
            )}
            {children && <Grid item>{children}</Grid>}
        </CenteredContainer>
    );
};

const SuccessPage = ({title = 'Success', message = null, children}) => {
    return (
        <CenteredContainer>
            <CenteredColumnItem>
                <SuccessIcon height='100px' width='100px' />
            </CenteredColumnItem>
            <CenteredColumnItem>
                <H4 gutterBottom={false}>{title}</H4>
            </CenteredColumnItem>
            {message && (
                <CenteredColumnItem>
                    <P>{message}</P>
                </CenteredColumnItem>
            )}
            {children && <CenteredColumnItem>{children}</CenteredColumnItem>}
        </CenteredContainer>
    );
};

const CloudErrorPage = ({
    title = 'Oops!',
    message = (
        <PSingleLine>
            Our wires got crossed. Please refresh the page and try again.
        </PSingleLine>
    ),
    error = null,
    children,
}) => {
    const development = process.env.NODE_ENV === 'development';

    if (!development && error) {
        window.dataLayer.push({
            event: 'data_load_error',
            data: {
                url: window.location.href,
                endpoint: error?.endpoint,
                status: error?.status,
            },
        });
    }

    return (
        <CenteredContainer>
            <CenteredColumnItem>
                <CloudErrorIcon height='100px' width='100px' />
            </CenteredColumnItem>
            <CenteredColumnItem>
                <H4 gutterBottom={false}>{title}</H4>
            </CenteredColumnItem>
            {message && <CenteredColumnItem>{message}</CenteredColumnItem>}
            {children && <CenteredColumnItem>{children}</CenteredColumnItem>}
            {development && error ? (
                <AlertDialog title='Data Load Error' message={error} />
            ) : null}
        </CenteredContainer>
    );
};

export {CenteredContainer, ErrorPage, SuccessPage, CloudErrorPage};
export default LoadingPage;
